import moment from 'moment'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { Case, CaseStatus } from '@debbie/common/dist/cases/index.js'
import { Customer } from '@debbie/common/dist/customers/index.js'
import { isAdmin } from '@debbie/common/dist/permissions/index.js'
import { c } from '@debbie/cortex/dist/index.js'
import { search } from '@debbie/cortex/dist/api/index.js'
import * as casesAPI from '@debbie/cortex/dist/api/cases.js'
import { selectRole } from '@debbie/cortex/dist/me/selectors.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import {
  Button,
  Modal,
  PageMenu,
  CaseStatus as CaseStatusStyled,
  MenuItem,
  CaseStatusSelector,
  SearchInput,
  ExclusiveContent,
  ButtonDrop,
  Well,
} from '@debbie/iris/dist/components/index.js'
import { Col } from '@debbie/iris/dist/components/Grid/index.js'
import {
  File,
  GitMerge,
  MoreVertical,
  Users,
  Zap,
} from '@debbie/iris/dist/components/Icons/index.js'
import Pause from '@debbie/iris/dist/components/Icons/Pause.js'
import Trash2 from '@debbie/iris/dist/components/Icons/Trash2.js'
import Shortcut from '@debbie/iris/dist/components/Shortcut.js'
import WorkflowStatus from '@debbie/iris/dist/components/WorkflowStatus.js'
import THEME from '@debbie/iris/dist/theme.js'
import SearchResponsible from '@debbie/iris/dist/components/SearchResponsible.js'

import { ActiveUsersOnCase } from './index.js'
import { Entity, Site } from '@debbie/common/dist/index.js'
import { BlameLogsContent } from '@debbie/iris/dist/components/BlameLog/BlameLog.js'
import { BlameLogRefType } from '@debbie/common/dist/blamelog/index.js'
import q from '@debbie/cortex/dist/query.js'
import { interactionsEntity } from '@debbie/cortex/dist/interactions/index.js'
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js'
import keys from '@debbie/common/dist/cache/keys.js'

type Props = {
  case: Case
  customer: Customer
  id: string
  setShowNewUpdate: (showNewUpdate: boolean) => void
  showNewUpdate: boolean
}

const dropdownStyle: React.CSSProperties = {
  textAlign: 'left',
  textTransform: 'initial',
}

const CaseHeader = (props: Props) => {
  const {
    customer,
    case: singleCase,
    id,
    setShowNewUpdate,
    showNewUpdate,
  } = props

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [mergeModalOpen, setMergeModalOpen] = useState<boolean>(false)
  const [blameLogsOpen, setBlameLogsOpen] = useState<boolean>(false)
  const [responsibleOpen, setresponsibleOpen] = useState<boolean>(false)
  const [merging, setMerging] = useState<boolean>(false)
  const [moreOptionsOpen, setMoreOptionsOpen] = useState<boolean>(false)
  const [toCase, setToCase] = useState<{
    address: string
    caseSeqId: number
    createdAt: string
    creditorId: string
    creditorName: string
    creditorReferenceId: string
    email: string
    isCompany: true
    name: string
    objectID: string
    ownerId: string
    phone: string
    status: string
    type: string
  } | null>(null)

  const [loading, setLoading] = useState(false)

  const lang = useSelector((s: RootState) => s.lang)
  const site = useSelector((s: RootState) => s.site)
  const meRole = useSelector(selectRole)

  const responsible =
    singleCase.responsibleType && singleCase.responsibleReferenceId
      ? {
          type: singleCase.responsibleType,
          referenceId: singleCase.responsibleReferenceId,
        }
      : null

  const toggleMergeModalOpen = () => {
    setMergeModalOpen(!mergeModalOpen)
  }
  const toggleDeleteModal = () => {
    setDeleteModalOpen(!deleteModalOpen)
  }

  const searchAll = async (q: string) => {
    if ((q && q.length <= 2) || !q) return []

    const re: any =
      singleCase &&
      (await search.all(q, {
        creditorId: singleCase.creditorId,
      }))

    return re.hits
      ? re.hits.map((result: any) => {
          return {
            heading: (
              <div style={{ display: 'inline-block' }}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: result._highlightResult.name.value,
                  }}
                />{' '}
                <CaseStatusStyled status={result.status} />
              </div>
            ),
            key: result.objectID,
            small: (
              <div
                dangerouslySetInnerHTML={{
                  __html: `${
                    result._highlightResult.creditorReferenceId
                      ? result._highlightResult.creditorReferenceId.value
                      : ''
                  } ${
                    result._highlightResult.migratedId &&
                    result._highlightResult.migratedId.value
                      ? `[${lang.search.migratedId}: ${
                          result._highlightResult.migratedId &&
                          result._highlightResult.migratedId.value
                        }] `
                      : ''
                  } #${result._highlightResult.caseSeqId.value}`,
                }}
              />
            ),
            supplementary: [
              {
                header: (
                  <span
                    style={{ float: 'left' }}
                    dangerouslySetInnerHTML={{
                      __html: result._highlightResult.creditorName.value,
                    }}
                  />
                ),
                small: moment(result.createdAt).format(THEME.DATE_FORMAT),
              },
            ],
            returnData: result,
          }
        })
      : []
  }

  return (
    <Col size="md" width="12">
      <PageMenu
        breadCrumb={[
          {
            text: lang.navigation.cases,
            path: '/cases',
          },
          {
            text: id ? `#${id}` : '',
            path: `/cases/${id}`,
            copyToClipBoard: id,
          },
        ]}
        contentLeft={
          <>
            {customer && singleCase && singleCase.workflowId && (
              <MenuItem>
                <WorkflowStatus case={singleCase} customer={customer} />
              </MenuItem>
            )}
            <ActiveUsersOnCase id={`${singleCase.id}`} />
          </>
        }
        mobileMenu={
          <Button
            size="small"
            className="ui-test-db701363"
            onClick={() => setShowNewUpdate(!showNewUpdate)}
            style={{ marginRight: '10px' }}
          >
            <Pause
              style={{
                margin: '2px 0 -2px',
              }}
            />
          </Button>
        }
        contentRight={
          singleCase && (
            <>
              <MenuItem>
                <CaseStatusSelector case={singleCase} />
              </MenuItem>
              <MenuItem>
                <Shortcut
                  keys={['command', 'k']}
                  onPress={(e: any) => {
                    e.preventDefault()
                    setShowNewUpdate(!showNewUpdate)
                  }}
                  style={{
                    left: '-15px',
                    top: '5px',
                  }}
                />
                <Button
                  size="small"
                  className="ui-test-db701363"
                  onClick={() => setShowNewUpdate(!showNewUpdate)}
                >
                  <Pause
                    style={{
                      margin: '2px 0 -2px',
                    }}
                  />{' '}
                  {lang.cases.pause}
                </Button>
              </MenuItem>
              <MenuItem>
                <CaseStatusStyled
                  status={singleCase.status}
                  statusTime={singleCase.statusTime}
                  endReasonId={singleCase.endReasonId}
                />
              </MenuItem>
              {site === Site.Caseworker && (
                <MenuItem>
                  <ButtonDrop
                    customButton={
                      <MoreVertical
                        style={{
                          width: '16px',
                          height: '16px',
                          marginLeft: '-20px',
                        }}
                      />
                    }
                    open={moreOptionsOpen}
                    onOpen={() => {
                      setMoreOptionsOpen(true)
                    }}
                    onClose={() => {
                      setMoreOptionsOpen(false)
                    }}
                    size="mini"
                  >
                    <Button
                      block
                      onClick={() => setBlameLogsOpen(!blameLogsOpen)}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <File
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.blameLogs.blameLog}
                    </Button>
                    <Button
                      block
                      onClick={() => setresponsibleOpen(!responsibleOpen)}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <Users
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.teams.responsibleForCase}
                    </Button>
                    <Button
                      block
                      onClick={toggleMergeModalOpen}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <GitMerge
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.mergeCases.mergeBtn}
                    </Button>
                    {meRole && isAdmin(meRole) && (
                      <Button
                        block
                        onClick={toggleDeleteModal}
                        theme="light"
                        style={dropdownStyle}
                      >
                        <Trash2
                          style={{
                            cursor: 'pointer',
                          }}
                        />{' '}
                        {lang.cases.deleteCase}
                      </Button>
                    )}
                    <ExclusiveContent>
                      <Button
                        block
                        onClick={() => {
                          if (singleCase) {
                            casesAPI.index(singleCase.caseId)
                          }
                        }}
                        theme="light"
                        style={dropdownStyle}
                      >
                        <Zap /> Index case in Algolia
                      </Button>
                    </ExclusiveContent>
                  </ButtonDrop>
                </MenuItem>
              )}
              {blameLogsOpen && (
                <Modal
                  onToggle={() => setBlameLogsOpen(!blameLogsOpen)}
                  header={lang.blameLogs.blameLog}
                  maxWidth="960px"
                >
                  {singleCase && (
                    <BlameLogsContent
                      refType={BlameLogRefType.Case}
                      refId={singleCase.caseId}
                    />
                  )}
                </Modal>
              )}

              {responsibleOpen && (
                <Modal
                  onToggle={() => setresponsibleOpen(!responsibleOpen)}
                  header={lang.teams.responsibleForCase}
                  maxWidth="650px"
                >
                  <Well>
                    <SearchResponsible
                      onChange={async (change) => {
                        try {
                          await casesAPI.patch(singleCase.caseId, {
                            responsibleType: change?.type || null,
                            responsibleReferenceId: change?.referenceId || null,
                          })
                          await c.cases.getById(singleCase.caseId)
                        } catch (error) {
                          console.error(error)
                          alert(error)
                        }
                      }}
                      responsible={responsible}
                    />
                  </Well>
                </Modal>
              )}

              {mergeModalOpen && singleCase && (
                <Modal
                  header={lang.mergeCases.header}
                  onToggle={toggleMergeModalOpen}
                  maxWidth="500px"
                >
                  <p>{lang.mergeCases.explanation}</p>
                  <SearchInput
                    searchDelay={300}
                    onSelect={setToCase}
                    placeholder={lang.mergeCases.searchPlaceholder}
                    searchAll={searchAll}
                    onRemoveStatic={() => setToCase(null)}
                    static={
                      toCase
                        ? `${toCase.name} - #${toCase.caseSeqId}`
                        : undefined
                    }
                  />
                  <Button
                    disabled={!(toCase && toCase.objectID)}
                    block
                    loading={merging}
                    onClick={async () => {
                      setMerging(true)
                      try {
                        if (!toCase?.objectID) {
                          throw 'Could not find case id'
                        }
                        await casesAPI.merge(singleCase.caseId, {
                          toCaseId: toCase.objectID,
                        })

                        const invalidateCaseCache = async (caseId: string) => {
                          await c.cases.getById(caseId)
                          await c.caseVouchers.getByCaseId(caseId)
                          await c.cases.getUpdates(caseId)
                          q.invalidate([
                            ...keys.write.create(caseVouchersEntity),
                            ...keys.write.create(interactionsEntity),
                          ])
                        }

                        // Invalidate from case cache
                        await invalidateCaseCache(singleCase.caseId)

                        // Invalidate to case cache
                        await invalidateCaseCache(toCase.objectID)
                      } catch (error) {
                        alert('Error')
                      }
                      setMergeModalOpen(false)
                      setToCase(null)
                      setMerging(false)
                    }}
                  >
                    {lang.mergeCases.mergeBtn}
                  </Button>
                </Modal>
              )}

              {deleteModalOpen && (
                <Modal
                  maxWidth="400px"
                  header={lang.cases.sureYouWantToDeleteCase}
                  onToggle={toggleDeleteModal}
                >
                  <Button
                    theme="dangerous"
                    loading={loading}
                    size="small"
                    block={true}
                    onClick={async () => {
                      setLoading(true)
                      await casesAPI.deleteCase(props.case.caseId)
                      await c.cases.getById(props.case.id)
                      setLoading(false)
                    }}
                  >
                    {lang.cases.deleteCase}
                  </Button>
                </Modal>
              )}
            </>
          )
        }
      />
    </Col>
  )
}

export default CaseHeader
